import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import {
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  Truck,
  MapPin,
  Calendar,
  ArrowRight,
  Filter,
} from "lucide-react";
import {
  getStatusBgColor,
  getStatusTextColor,
} from "@shared/utils/statusColors";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import * as packagesService from "@/features/packages/services/packagesService";
import LivreurPickupStatusUpdateDialog from "@/features/packages/components/LivreurPickupStatusUpdateDialog";

const LivreurPickupManagementWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch livreur pickups
  const {
    data: pickupData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["livreur-pickups-widget", statusFilter],
    queryFn: async () => {
      const params =
        statusFilter !== "all" ? `status=${statusFilter}&limit=5` : "limit=5";
      return packagesService.getLivreurPickups(params);
    },
    select: (data) => data?.pickups || [],
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "approved":
        return <CheckCircle className="h-4 w-4" />;
      case "rejected":
        return <AlertTriangle className="h-4 w-4" />;
      case "collected":
        return <Package className="h-4 w-4" />;
      case "in_transit":
        return <Truck className="h-4 w-4" />;
      case "delivered":
        return <CheckCircle className="h-4 w-4" />;
      case "failed":
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const filterOptions = [
    { value: "all", label: "pickup.allPickups" },
    { value: "approved", label: "pickup.approved" },
    { value: "collected", label: "pickup.collected" },
    { value: "in_transit", label: "pickup.inTransit" },
    { value: "delivered", label: "pickup.delivered" },
  ];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            {t("pickup.myPickups")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            {t("pickup.myWarehousePickups")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const nextIndex =
                  (filterOptions.findIndex((f) => f.value === statusFilter) +
                    1) %
                  filterOptions.length;
                setStatusFilter(filterOptions[nextIndex].value);
              }}
              className="text-xs"
            >
              <Filter className="h-3 w-3 mr-1" />
              {t(filterOptions.find((f) => f.value === statusFilter)?.label)}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/pickups/management")}
              className="text-muted-foreground hover:text-foreground"
            >
              {t("common.viewAll")}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {!pickupData || pickupData.length === 0 ? (
            <div className="text-center py-6">
              <Truck className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold mb-2">
                {t("pickup.noPickupsFound")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {statusFilter !== "all"
                  ? t("pickup.noPickupsMatchCurrentFilter")
                  : t("pickup.youDontHaveAnyAssignedPickupsYet")}
              </p>
            </div>
          ) : (
            <>
              {pickupData.map((pickup) => (
                <div
                  key={pickup._id}
                  className="p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                        {getStatusIcon(pickup.status)}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {pickup.type === "package"
                              ? t("pickup.packagePickup")
                              : t("pickup.productPickup")}
                          </span>
                          <Badge
                            variant="outline"
                            className={`${getStatusBgColor(
                              pickup.status
                            )} ${getStatusTextColor(pickup.status)}`}
                          >
                            {pickup.status?.replace(/_/g, " ").toUpperCase()}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            <span>{pickup.pickupCity}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>
                              {format(new Date(pickup.updatedAt), "MMM dd")}
                            </span>
                          </div>
                          {pickup.type === "package" && (
                            <span>
                              {pickup.packages?.length || 0}{" "}
                              {t("common.packages")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <LivreurPickupStatusUpdateDialog
                      pickupData={pickup}
                      onUpdate={refetch}
                      trigger={
                        <Button size="sm" variant="outline">
                          {t("pickup.update")}
                        </Button>
                      }
                    />
                  </div>

                  {/* Pickup Details */}
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-muted-foreground">
                        {t("pickup.contact")}:
                      </span>
                      <div className="font-medium">{pickup.name}</div>
                      <div className="text-muted-foreground">
                        {pickup.phone}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {t("pickup.address")}:
                      </span>
                      <div className="font-medium">{pickup.address}</div>
                    </div>
                  </div>

                  {/* Note if exists */}
                  {pickup.note && (
                    <div className="mt-2 p-2 bg-muted rounded text-xs">
                      <span className="font-medium">{t("common.note")}: </span>
                      <span className="text-muted-foreground">
                        {pickup.note}
                      </span>
                    </div>
                  )}
                </div>
              ))}

              {/* View All Button */}
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() =>
                    navigate("/packages/livreur/dashboard?tab=pickups")
                  }
                >
                  <Truck className="h-4 w-4 mr-2" />
                  {t("pickup.viewAllMyPickups")}
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LivreurPickupManagementWidget;
